import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import type { IComment } from "../../../../../types/comment";
import { useState } from "react";
import apiService from "../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { handleExport } from "../utils/handleCommentExport";
import { AddContactModal } from "./AddContactModal";
import { EditCommentModal } from "./EditCommentModal";
import { tokens } from "../../../../../theme";

interface CommentMobileCardProps {
  comment: IComment;
  refreshComments: (comment: IComment, isDeleting: boolean) => void;
  fetchApprovedComments: () => Promise<void>;
  existingContactEmails: string[];
  isLast?: boolean;
}

export const CommentMobileCard: React.FC<CommentMobileCardProps> = ({
  comment,
  refreshComments,
  fetchApprovedComments,
  existingContactEmails,
  isLast = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const isConverted = existingContactEmails.includes(
    comment.authorEmail?.toLowerCase() ?? "",
  );

  const deleteComment = async () => {
    setIsDeleting(true);
    try {
      await apiService.delete(`/api/chats/memorial-message/${comment.id}/`);
      refreshComments(comment, true);
      showSuccessToast("Comment deleted");
    } catch {
      showErrorToast("Failed to delete the comment, please try again");
    } finally {
      setIsDeleting(false);
    }
  };

  const updateApproveStatus = async () => {
    setIsStatusUpdating(true);
    try {
      await apiService.patch(`/api/chats/memorial-message/${comment.id}/`, {
        is_approved: !comment.isApproved,
      });
      const updatedComment = { ...comment, isApproved: !comment.isApproved };
      refreshComments(updatedComment, false);
      showSuccessToast(
        `Comment ${updatedComment.isApproved ? "Approved" : "Unapproved"}`,
      );
      fetchApprovedComments();
    } catch (error) {
      console.error(error);
      showErrorToast(
        `Failed to ${comment.isApproved ? "unapprove" : "approve"} comment.`,
      );
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const isBusy = isStatusUpdating || isDeleting;

  return (
    <>
      {addContactModalOpen && (
        <AddContactModal
          comment={comment}
          setAddContactModalOpen={setAddContactModalOpen}
        />
      )}

      {editModalOpen && (
        <EditCommentModal
          comment={comment}
          open={editModalOpen}
          setOpen={setEditModalOpen}
          refreshComments={refreshComments}
        />
      )}

      <Card
        variant="outlined"
        sx={{
          mb: 1.5,
          borderRadius: 2,
          transition: "box-shadow 0.15s ease",
          "&:active": { transform: "scale(0.995)" },
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          {/* Top row — unchanged */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <PersonOutlineIcon
                fontSize="small"
                sx={{ opacity: 0.6, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{ fontSize: "0.875rem" }}
              >
                {comment.author}
              </Typography>
            </Box>
            <Chip
              label={comment.isApproved ? "Approved" : "Pending"}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: comment.isApproved ? "#4BA663" : "#F79E1B40",
                color: comment.isApproved ? "#fff" : "inherit",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          </Box>

          {comment.authorEmail && comment.authorEmail !== "Anonymous" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
              <EmailOutlinedIcon sx={{ fontSize: "0.8rem", opacity: 0.5 }} />
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {comment.authorEmail}
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                boxShadow: 0,
                bgcolor: colors.primary[900],
                border: "1px solid #f1f5f9",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#475569",
                  lineHeight: 1.6,
                  mb: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {comment.content}
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                  {comment.isApproved ? (
                    <Box component="span" sx={{ fontWeight: 700, color: "#475569" }}>
                      Approved
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: "#cbd5e1" }}>
                      Not yet approved
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                  {comment.timestamp}
                </Typography>
              </Stack>
            </Box>

            {!isLast && (
              <Box
                sx={{
                  width: 2,
                  height: 16,
                  bgcolor: "action.hover",
                  mx: "auto",
                }}
              />
            )}
          </Box>

          {isBusy ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 1,
              }}
            >
              <Button
                variant={comment.isApproved ? "outlined" : "contained"}
                color={comment.isApproved ? "secondary" : "success"}
                size="small"
                onClick={updateApproveStatus}
                sx={{ fontSize: "0.72rem", px: 0.5 }}
              >
                {comment.isApproved ? "Unapprove" : "Approve"}
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => setEditModalOpen(true)}
                sx={{ fontSize: "0.72rem", px: 0.5 }}
              >
                <EditIcon sx={{ fontSize: "0.9rem", mr: 0.25 }} />
                Edit
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={deleteComment}
                sx={{ fontSize: "0.72rem", px: 0.5 }}
              >
                Delete
              </Button>

              <Box
                sx={{
                  gridColumn: "span 3",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleExport("csv", comment)}
                  sx={{ fontSize: "0.72rem", px: 0.5 }}
                >
                  <DownloadIcon sx={{ fontSize: "0.9rem", mr: 0.25 }} />
                  CSV
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={!comment.authorEmail || isConverted}
                  onClick={() => setAddContactModalOpen(true)}
                  sx={{ fontSize: "0.72rem", px: 0.5 }}
                >
                  <AddIcon sx={{ fontSize: "0.9rem", mr: 0.25 }} />
                  {isConverted ? "Done" : "Contact"}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};
